import { useValidationForm } from './useValidationForm';
import { useDispatch, useSelector } from 'react-redux';
import {
  createPayment as createPaymentThunk,
  draftInvoicePlanSelector
} from '../redux/features/invoice/invoicesSlice';
import {
  PLAN_TYPE,
  PRODUCT_PLANS_INTERVAL_MONTH,
  PRODUCT_PLANS_TYPE_OPTION_RADIO
} from '../config';
import { useCallback, useEffect, useState } from 'react';
import { useProductPlans } from './useProductPlans';
import {
  currentFileLogoUrlSelector,
  draftFileLogoUrlSelector
} from '../redux/features/file/filesSlice';
import { draftJobSelector } from '../redux/features/job/jobsSlice';
import {
  currentCompanySelector,
  draftCompanySelector
} from '../redux/features/company/companiesSlice';
import { getLocalStorageAuthentication } from '../lib/auth';
import { trackEvent } from '../helpers';

const schemaToValidate = {};

schemaToValidate.totalSelectedPlans = {
  presence: { allowEmpty: true }
};

export const usePlanPackageForm = (preselectedJobId = null, options = {}) => {
  const { disableJobAssociation = false } = options;
  const dispatch = useDispatch();
  const draftInvoicePlan = useSelector(draftInvoicePlanSelector);

  // Get product plans with real-time pricing from Redux
  const PRODUCT_PLANS = useProductPlans();

  // Get draft data
  const draftFileLogoUrl = useSelector(draftFileLogoUrlSelector);
  const draftJob = useSelector(draftJobSelector);
  const draftCompany = useSelector(draftCompanySelector);
  // Get current data (fallback when draft is empty)
  const currentFileLogoUrl = useSelector(currentFileLogoUrlSelector);
  const currentCompany = useSelector(currentCompanySelector);
  // Use draft if available, otherwise fallback to current
  const effectiveCompany =
    draftCompany && draftCompany.id ? draftCompany : currentCompany;
  const effectiveFileLogoUrl = draftFileLogoUrl || currentFileLogoUrl;
  console.log('🔍 [usePlanPackageForm] Draft company:', draftCompany);
  console.log('🔍 [usePlanPackageForm] Current company:', currentCompany);
  console.log('🔍 [usePlanPackageForm] Effective company:', effectiveCompany);
  console.log('🔍 [usePlanPackageForm] Draft file URL:', draftFileLogoUrl);
  console.log('🔍 [usePlanPackageForm] Current file URL:', currentFileLogoUrl);
  console.log(
    '🔍 [usePlanPackageForm] Effective file URL:',
    effectiveFileLogoUrl
  );

  const {
    handleChange,
    hasError,
    formState,
    isValidForm,
    handleChangeCustomFieldName
  } = useValidationForm({
    schemaToValidate,
    afterValidateOnChangeFn: (updatedState) => {
      // dispatch(
      //   draftCompanySessionUpdated({
      //     company: updatedState
      //   })
      // );
    },
    initValues: draftInvoicePlan
  });

  // When preselectedJobId is present OR draftJob exists, always include POST_JOB plan
  const getInitialPlans = useCallback(() => {
    if (disableJobAssociation) {
      return [];
    }

    const defaultPlans = PRODUCT_PLANS.filter(
      (p) => p.isDefaultChecked === true
    );

    // Check if we should auto-select POST_JOB (either from URL param or from draft)
    const shouldAutoSelectPostJob =
      !disableJobAssociation && (preselectedJobId || (draftJob && draftJob.id));

    if (shouldAutoSelectPostJob) {
      // Ensure POST_JOB plan is included
      const postJobPlan = PRODUCT_PLANS.find(
        (p) => p.id === PLAN_TYPE.POST_JOB
      );
      if (
        postJobPlan &&
        !defaultPlans.find((p) => p.id === PLAN_TYPE.POST_JOB)
      ) {
        console.log(
          '💳 [usePlanPackageForm] Auto-selecting POST_JOB plan for job:',
          preselectedJobId || draftJob?.id
        );
        return [...defaultPlans, postJobPlan];
      }
    }

    return defaultPlans;
  }, [preselectedJobId, draftJob, disableJobAssociation, PRODUCT_PLANS]);

  const [allSelectedPlans, setAllTotalSelectedPlans] = useState(
    getInitialPlans()
  );

  // Re-evaluate plans when draftJob or preselectedJobId changes
  useEffect(() => {
    setAllTotalSelectedPlans(getInitialPlans());
  }, [draftJob, preselectedJobId, getInitialPlans]); // getInitialPlans is now stable via useCallback

  const [showOnTopDemo, setShowOnTopDemo] = useState(false);
  const [showMyLogo, setShowMyLogo] = useState(true);
  const [highlightWithYellow, setHighlightWithYellow] = useState(false);
  const [brandColor, setBrandColor] = useState('#FF6B6B');
  const [highlightWithBrandColor, setHighlightWithBrandColor] = useState(false);
  const totalSelectedPlans = allSelectedPlans.reduce((acc, item) => {
    return (
      acc +
      item.prices.filter((p) => PRODUCT_PLANS_INTERVAL_MONTH === p.interval)[0]
        .price
    );
  }, 0);
  const allSelectedPlanIDs = allSelectedPlans.reduce((acc, item) => {
    acc.push({
      price: item.prices.filter(
        (p) => PRODUCT_PLANS_INTERVAL_MONTH === p.interval
      )[0].id
    });
    return acc;
  }, []);
  const onChangePlanSelectionCheckList = (updatedSelectedPlans) => {
    // Make yellow and brand color highlights mutually exclusive
    let finalSelectedPlans = [...updatedSelectedPlans];
    const hasYellow = finalSelectedPlans.some(
      (p) => p.id === PLAN_TYPE.HIGHLIGHT_YELLOW
    );
    const hasBrandColor = finalSelectedPlans.some(
      (p) => p.id === PLAN_TYPE.HIGHLIGHT_COMPANY_COLOR
    );

    // If both are selected, remove the one that was already in the previous selection
    if (hasYellow && hasBrandColor) {
      const wasYellowSelected = allSelectedPlans.some(
        (p) => p.id === PLAN_TYPE.HIGHLIGHT_YELLOW
      );
      const wasBrandColorSelected = allSelectedPlans.some(
        (p) => p.id === PLAN_TYPE.HIGHLIGHT_COMPANY_COLOR
      );

      if (wasYellowSelected) {
        // Yellow was already selected, user just clicked brand color, so remove yellow
        finalSelectedPlans = finalSelectedPlans.filter(
          (p) => p.id !== PLAN_TYPE.HIGHLIGHT_YELLOW
        );
      } else if (wasBrandColorSelected) {
        // Brand color was already selected, user just clicked yellow, so remove brand color
        finalSelectedPlans = finalSelectedPlans.filter(
          (p) => p.id !== PLAN_TYPE.HIGHLIGHT_COMPANY_COLOR
        );
      }
    }

    setAllTotalSelectedPlans([...finalSelectedPlans]);

    // Track plan selection changes in Google Analytics
    const totalPrice = finalSelectedPlans.reduce((acc, item) => {
      return (
        acc +
        item.prices.filter(
          (p) => PRODUCT_PLANS_INTERVAL_MONTH === p.interval
        )[0].price
      );
    }, 0);

    trackEvent('plan_selection_changed', {
      plans_selected: finalSelectedPlans.map((p) => p.title).join(', '),
      plan_count: finalSelectedPlans.length,
      total_price: totalPrice,
      has_logo: finalSelectedPlans.some(
        (p) => p.id === PLAN_TYPE.SHOW_COMPANY_LOGO
      ),
      has_yellow_highlight: finalSelectedPlans.some(
        (p) => p.id === PLAN_TYPE.HIGHLIGHT_YELLOW
      ),
      has_brand_color_highlight: finalSelectedPlans.some(
        (p) => p.id === PLAN_TYPE.HIGHLIGHT_COMPANY_COLOR
      )
    });

    const isMyLogoSelected =
      finalSelectedPlans.filter((p) => p.id === PLAN_TYPE.SHOW_COMPANY_LOGO)
        .length > 0;
    isMyLogoSelected ? setShowMyLogo(true) : setShowMyLogo(false);

    const isHighlightYellowSelected =
      finalSelectedPlans.filter((p) => p.id === PLAN_TYPE.HIGHLIGHT_YELLOW)
        .length > 0;
    isHighlightYellowSelected
      ? setHighlightWithYellow(true)
      : setHighlightWithYellow(false);

    const isHighlightBrandColorSelected =
      finalSelectedPlans.filter(
        (p) => p.id === PLAN_TYPE.HIGHLIGHT_COMPANY_COLOR
      ).length > 0;
    isHighlightBrandColorSelected
      ? setHighlightWithBrandColor(true)
      : setHighlightWithBrandColor(false);
  };

  const onBrandColorChange = (color) => {
    setBrandColor(color);
    // When a brand color is selected, automatically enable brand color highlighting
    // and disable yellow highlight (they are mutually exclusive)
    if (color) {
      setHighlightWithBrandColor(true);
      setHighlightWithYellow(false); // Disable yellow when brand color is selected
    }
  };

  const onChangePlanSelectionRadioList = (item, removeItem) => {
    if (item && allSelectedPlans.filter((p) => p.id === item.id).length < 1) {
      const filteredCleanUpRadioItems = allSelectedPlans.filter(
        (p) => p.typeOption !== PRODUCT_PLANS_TYPE_OPTION_RADIO
      );
      setAllTotalSelectedPlans([...filteredCleanUpRadioItems, item]);
      setShowOnTopDemo(true);

      // Track sticky plan selection
      trackEvent('sticky_plan_selected', {
        plan_name: item.title,
        plan_id: item.id,
        plan_price: item.prices.filter(
          (p) => PRODUCT_PLANS_INTERVAL_MONTH === p.interval
        )[0].price
      });
    }
    if (removeItem) {
      const filteredItems = allSelectedPlans.filter(
        (p) => p.id !== removeItem.id
      );
      setAllTotalSelectedPlans([...filteredItems]);
      setShowOnTopDemo(false);
    }
  };

  const createSubscription = async (
    email,
    paymentMethodId,
    items,
    jobId,
    profilePlanId = null
  ) => {
    try {
      const localAuth = getLocalStorageAuthentication();

      // Check if geo-lock plan is selected
      const hasGeoLock = allSelectedPlans.some(
        (p) => p.id === PLAN_TYPE.GEO_LOCK
      );

      const selectedPlansForMetadata = allSelectedPlans
        .filter((plan) =>
          [
            PLAN_TYPE.HIRING_PROFILE_STARTER,
            PLAN_TYPE.HIRING_PROFILE_PROFESSIONAL,
            PLAN_TYPE.HIRING_PROFILE_PREMIUM
          ].includes(plan.id)
        )
        .map((plan) => ({
          id: plan.id,
          name: plan.name,
          description: plan.description
        }));

      // Build metadata object from selected plan features
      const metadata = disableJobAssociation
        ? {
            selectedPlans: selectedPlansForMetadata
          }
        : {
            showLogo: showMyLogo,
            brandColor: brandColor,
            highlightYellow: highlightWithYellow,
            highlightBrandColor: highlightWithBrandColor,
            showOnTop: showOnTopDemo,
            geoLockEnabled: hasGeoLock, // Add geo-lock flag
            allowedRegions: hasGeoLock ? draftJob?.regionalRestrictions : null, // Store allowed regions if geo-locked
            selectedPlans: allSelectedPlans.map((p) => ({
              id: p.id,
              name: p.name,
              description: p.description
            }))
          };

      // Use the provided profilePlanId (from tier selection) or extract from allSelectedPlans
      // Priority: parameter > allSelectedPlans
      let finalProfilePlanId = profilePlanId;

      if (!finalProfilePlanId) {
        // Extract hiring profile plan ID if present (9=Starter, 10=Professional, 11=Premium)
        const hiringProfilePlan = allSelectedPlans.find(
          (p) =>
            p.id === PLAN_TYPE.HIRING_PROFILE_STARTER ||
            p.id === PLAN_TYPE.HIRING_PROFILE_PROFESSIONAL ||
            p.id === PLAN_TYPE.HIRING_PROFILE_PREMIUM
        );
        finalProfilePlanId = hiringProfilePlan?.id || null;
      }

      console.log('📦 [Payment] Plan metadata:', metadata);
      console.log('📌 [Payment] Job ID:', jobId || preselectedJobId);
      console.log('🔒 [Payment] Geo-lock enabled:', hasGeoLock);
      console.log('🆔 [Payment] Profile Plan ID (final):', finalProfilePlanId);
      console.log('💳 [Payment] Items to charge:', items);

      const jobIdToUse = disableJobAssociation
        ? null
        : jobId || preselectedJobId || draftJob?.id;

      dispatch(
        createPaymentThunk({
          userId: localAuth.userId,
          email: email || localAuth.email, // Use provided email or fallback to JWT email
          payment_method: paymentMethodId,
          currency: 'usd', // Default currency
          items, // Send the single discounted job post price from PlanPackage
          job_id: jobIdToUse, // Associate job only when needed
          metadata, // Pass the plan metadata
          plan_id: finalProfilePlanId // Pass the hiring profile plan ID for benefit application
        })
      );

      // Payment success is now handled by Redux thunk
      // The thunk will dispatch createSubscriptionSucceeded and show success message
      // Navigation will be handled by the component watching the subscription state
      console.log('✅ [Payment] Payment thunk dispatched successfully');
    } catch (error) {
      console.error('Failed to create payment:', error);
    }
  };

  const getUserEmail = () => {
    const localAuth = getLocalStorageAuthentication();
    return localAuth?.email || '';
  };

  return {
    handleChange,
    handleChangeCustomFieldName,
    hasError,
    formValues: formState.values,
    formErrors: formState.errors,
    isValidForm,
    onChangePlanSelectionCheckList,
    onChangePlanSelectionRadioList,
    onBrandColorChange,
    createSubscription,
    userEmail: getUserEmail(),
    draftFileLogoUrl: effectiveFileLogoUrl, // Use effective (draft or current)
    draftJob,
    draftCompany: effectiveCompany, // Use effective (draft or current)
    totalSelectedPlans,
    allSelectedPlanIDs,
    allSelectedPlans,
    showOnTopDemo,
    setShowOnTopDemo,
    showMyLogo,
    highlightWithYellow,
    setHighlightWithYellow,
    brandColor,
    highlightWithBrandColor,
    setHighlightWithBrandColor
  };
};
